import { useMemo } from "react";
import { common } from "@shared/constants";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { useCheckoutForms } from "@shared/modules/checkout/hooks/useCheckoutForms";
import { WizardStep } from "@shared/components/application/navigation/Wizard/WizardStep.web";
import { useEnhancedInfo } from "@shared/modules/checkout/hooks/useEnhancedInfo";
import CustomQuestions from "@app-contexts/public/shared/checkout/components/steps/custom-questions/CustomQuestions.web";
import { useCustomQuestions } from "@shared/modules/checkout/hooks/useCustomQuestions";
import { useTranslation } from "@shared/utils/internationalization";

export const CUSTOM_QUESTIONS_STEP_ID = "custom-questions";

export function CustomQuestionsStep({
  hideContinueButton,
}: {
  hideContinueButton?: boolean;
}) {
  const { t } = useTranslation("checkout");
  // Get the basket information to determine if this needs to be included as a step
  const { itemGroups, isGroupSale } = usePublicBasket();
  const { hasEnhancedInfo, hasAddressInfo } = useEnhancedInfo(
    common.DataProvider.Public,
    true
  );
  const { hasCustomQuestions } = useCustomQuestions(
    common.DataProvider.Public,
    true
  );
  const hasMultiGuestItemGroups = useMemo(() => {
    return (
      itemGroups.filter((ig) => ig.info_required).length > 0 && !isGroupSale
    );
  }, [itemGroups, isGroupSale]);

  const isCustomQuestionsNeeded = useMemo(() => {
    // Don't show custom questions step for group sales
    if (isGroupSale) {
      return false;
    }
    return (
      hasEnhancedInfo() ||
      hasAddressInfo() ||
      hasCustomQuestions() ||
      hasMultiGuestItemGroups
    );
  }, [
    isGroupSale,
    hasEnhancedInfo,
    hasAddressInfo,
    hasCustomQuestions,
    hasMultiGuestItemGroups,
  ]);

  const checkoutForms = useCheckoutForms();

  return (
    <>
      <WizardStep
        stepId={CUSTOM_QUESTIONS_STEP_ID}
        stepName={t("Questions", { ns: "common" })}
        shouldRegister={isCustomQuestionsNeeded}
        defaultIsValid={false}
      >
        {({ onValid, onInvalid }) => (
          <CustomQuestions
            checkoutForms={checkoutForms}
            onValid={onValid}
            onInvalid={onInvalid}
            hideContinueButton={hideContinueButton}
          />
        )}
      </WizardStep>
    </>
  );
}

CustomQuestionsStep.id = CUSTOM_QUESTIONS_STEP_ID;
